import React, {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { SettingTypeEnum } from '../enums/SettingTypeEnum';
import { useAuth } from '../hooks/useAuth';
import { useTags } from '../hooks/useTags';
import type Tag from '../interfaces/Tag';
import { FileService } from '../services/fileService';

interface PermissionsContextType {
  receivedFilesCount: number;
  getCountFilesUnread: () => Promise<void>;
  fileSizeLimitUpload: number;
  tags: Tag[];
  isCurrentFolderReadOnly: boolean;
  setIsCurrentFolderReadOnly: (isCurrentFolderReadOnly: boolean) => void;
  setFileSizeLimitUpload: (fileSizeLimitUpload: number) => void;
}

export const PermissionsContext = createContext<PermissionsContextType>({
  getCountFilesUnread: async () => {},
  receivedFilesCount: 0,
  fileSizeLimitUpload: 250,
  tags: [],
  isCurrentFolderReadOnly: false,
  setIsCurrentFolderReadOnly: () => {},
  setFileSizeLimitUpload: () => {},
});

const fileService = new FileService();

interface PermissionsProviderProps {
  children: ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const { isLoggedIn, authenticatedUser, isAdminOrContributor } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { tags } = useTags();
  const [receivedFilesCount, setReceivedFilesCount] = useState(0);
  const [fileSizeLimitUpload, setFileSizeLimitUpload] = useState<number>(250);
  const [isCurrentFolderReadOnly, setIsCurrentFolderReadOnly] = useState<boolean>(false);

  /**
   * @description Get the count of files unread.
   */
  const getCountFilesUnread = useCallback(async () => {
    if (!isLoggedIn) {
      return;
    }

    await fileService
      .getCountFilesUnread()
      .then((response) => {
        setReceivedFilesCount(response);
      })
      .catch(() => {
        console.error('Error getting count files unread');
      });
  }, [isLoggedIn]);

  /**
   * @description Get the count of files unread on component mount.
   */
  useEffect(() => {
    // Check if location is /file/:id and avoid getting brandingData
    if (location.pathname.includes('/file/')) {
      return;
    }

    getCountFilesUnread().then(() => {});
  }, [getCountFilesUnread, isLoggedIn]);

  useEffect(() => {
    const routesBlacklist = [
      '/files/personal/*',
      '/files/shared/*',
      '/files/trash',
      '/users/*',
      '/groups',
      '/settings/*',
    ];

    const path = location.pathname;
    const isBlacklisted = routesBlacklist.some((pattern) =>
      matchPath({ path: pattern, end: false }, path),
    );

    if (isBlacklisted && !isAdminOrContributor) {
      navigate('/');
    }
  }, [isAdminOrContributor, location.pathname]);

  useEffect(() => {
    if (authenticatedUser?.settings) {
      for (const setting of authenticatedUser.settings) {
        let parsedAdditionalData;
        try {
          if (!setting.additionalData) continue; // Skip if null/undefined/empty
          parsedAdditionalData = JSON.parse(setting.additionalData);
        } catch (err) {
          continue; // Jump to the next setting
        }
        if (parsedAdditionalData?.type === SettingTypeEnum.FileSizeLimit) {
          setFileSizeLimitUpload(parsedAdditionalData.fileSizeLimit);
        }
      }
    }
  }, [authenticatedUser]);

  const contextValue = useMemo(() => {
    return {
      getCountFilesUnread,
      receivedFilesCount,
      fileSizeLimitUpload,
      tags,
      isCurrentFolderReadOnly,
      setIsCurrentFolderReadOnly,
      setFileSizeLimitUpload,
    };
  }, [
    getCountFilesUnread,
    receivedFilesCount,
    fileSizeLimitUpload,
    tags,
    isCurrentFolderReadOnly,
    setIsCurrentFolderReadOnly,
    setFileSizeLimitUpload,
  ]);

  return <PermissionsContext.Provider value={contextValue}>{children}</PermissionsContext.Provider>;
};
